import type { ParseKeys } from "i18next";

import {
  CITY_OPTIONS,
  GROSS_NET_OPTIONS,
  INCOME_TYPE_OPTIONS,
  PAY_FREQUENCY_OPTIONS,
  US_STATE_OPTIONS,
} from "./constants";
import type { IncomeInput } from "./Signers.schema";

// ---------------------------------------------------------------------------
// Field kind discriminated union — used by IncomeBlock to render correctly
// ---------------------------------------------------------------------------
// Select options carry a resolved display label (built in constants.ts — i18n
// option labels are translated there; states/cities are literal reference data).
type SelectOption = { value: string; label: string };

type BaseField = {
  name: keyof IncomeInput;
  /** i18n key (resolved via `t()` in IncomeBlock), not display text. */
  label: ParseKeys;
  required?: boolean;
  /** i18n key for the placeholder shown inside the input. */
  placeholder?: ParseKeys;
  /** Visually hide the label (kept for screen readers via sr-only). */
  hideLabel?: boolean;
  /** Extra classes for the input/select control (e.g. a fixed width). */
  controlClassName?: string;
  /** Extra classes for the field label (e.g. whitespace-nowrap). */
  labelClassName?: string;
  /** Extra classes for the FormItem wrapper (e.g. label-left layout at lg). */
  itemClassName?: string;
};

type TextField = BaseField & { kind: "text" | "phone" | "date" };
type NumberField = BaseField & { kind: "number" };
type TextareaField = BaseField & { kind: "textarea" };
type SelectField = BaseField & {
  kind: "select";
  options: readonly SelectOption[];
  /** Suppress the "Select <label>" placeholder (e.g. when the field has a default). */
  hidePlaceholder?: boolean;
};
type CheckboxField = BaseField & { kind: "checkbox"; checkLabel: string };

export type IncomeFieldConfig = TextField | NumberField | TextareaField | SelectField | CheckboxField;

// ---------------------------------------------------------------------------
// Ordered list of all income fields
// ---------------------------------------------------------------------------
// `label`/`placeholder`/`checkLabel` hold i18n keys (resolved with `t()` in
// IncomeBlock), not display strings. Copy lives in src/languages/en.json.
export const INCOME_FIELDS: IncomeFieldConfig[] = [
  {
    kind: "select",
    name: "incomeType",
    label: "review.fields.incomeType",
    required: true,
    // Full list is the fallback; IncomeBlock overrides these options at render
    // time with getIncomeTypeOptions(applicantSsnType) to gate them by the
    // applicant's SSN/ITIN status (OR-21950).
    options: INCOME_TYPE_OPTIONS,
  },
  {
    kind: "text",
    name: "employerName",
    label: "review.fields.employerName",
    required: true,
  },
  {
    kind: "text",
    name: "occupation",
    label: "review.fields.occupation",
  },
  {
    kind: "text",
    name: "streetNumber",
    label: "review.fields.streetNumber",
    placeholder: "review.fields.streetNumber",
    hideLabel: true,
  },
  {
    kind: "text",
    name: "streetName",
    label: "review.fields.streetName",
    placeholder: "review.fields.streetName",
    hideLabel: true,
  },
  {
    kind: "text",
    name: "suite",
    label: "review.fields.suite",
    placeholder: "review.fields.suite",
    hideLabel: true,
  },
  {
    kind: "text",
    name: "zip",
    label: "review.fields.zip",
  },
  {
    kind: "select",
    name: "city",
    label: "review.fields.city",
    options: CITY_OPTIONS,
  },
  {
    kind: "select",
    name: "state",
    label: "review.fields.state",
    options: US_STATE_OPTIONS,
  },
  // verificationPhone + verificationPhoneExt are rendered together by the
  // dedicated PhoneExtField component (label-left at lg, grouped inputs), not
  // via the generic FieldRenderer — so they intentionally carry no layout props.
  {
    kind: "phone",
    name: "verificationPhone",
    label: "review.fields.verificationPhone",
    required: true,
  },
  {
    kind: "text",
    name: "verificationPhoneExt",
    label: "review.fields.verificationPhoneExt",
  },
  {
    kind: "checkbox",
    name: "differentWorkLocation",
    label: "review.fields.workLocation",
    checkLabel: "review.income.differentWorkLocation",
  },
  {
    kind: "number",
    name: "grossMonthlyPay",
    label: "review.fields.grossMonthlyPay",
    required: true,
    controlClassName: "w-32",
  },
  {
    kind: "date",
    name: "hireDate",
    label: "review.fields.hireDate",
    required: true,
    // pr-1: trim the right padding so the native calendar picker isn't pushed
    // far off the right border (date inputs render the picker at the padding edge).
    controlClassName: "w-32 pr-1",
  },
  {
    kind: "number",
    name: "payPerPeriod",
    label: "review.fields.payPerPeriod",
    controlClassName: "w-32",
  },
  {
    kind: "select",
    name: "payFrequency",
    label: "review.fields.payFrequency",
    options: PAY_FREQUENCY_OPTIONS,
    controlClassName: "w-36",
    hidePlaceholder: true,
  },
  {
    kind: "select",
    name: "grossNet",
    label: "review.fields.grossNet",
    options: GROSS_NET_OPTIONS,
    controlClassName: "w-32",
  },
  {
    kind: "textarea",
    name: "additionalInformation",
    label: "review.fields.additionalInformation",
    // single-row height (override the textarea's default min-h-14)
    controlClassName: "min-h-0",
  },
];
